import * as React from "react";
import {useEffect, useState} from "react";
import {
    Button,
    Field,
    tokens,
    makeStyles,
    SearchBox,
    TeachingPopover,
    TeachingPopoverTrigger,
    TeachingPopoverSurface,
    TeachingPopoverBody,
    TeachingPopoverTitle,
    TeachingPopoverFooter,
    Checkbox,
    CardHeader,
    Card,
    Text,
    useId,
    useToastController,
    Toast,
    ToastTitle,
    ToastBody,
    Toaster,
    Tooltip, Spinner
} from "@fluentui/react-components";
import {Checkmark16Filled, PersonMail16Filled} from "@fluentui/react-icons";
import {useDossierService, useMailService} from "../../utils/services/http.service";
import {WebMails} from "../../utils/models/webmail.model";
import {
    addCategoryToCurrentItem,
    addCategoryToItemId,
    buildWebMailFromCurrentItem,
    buildWebMailFromItemId,
    getSelectedMailRefs,
    isLoadItemByIdSupported,
    isMultiSelectSupported,
    registerSelectedItemsChanged,
    SelectedMailRef,
} from "../../utils/outlook-mail.helper";

const useStyles = makeStyles({
    root: {
        alignItems: "flex-start",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "50px 20px",
        rowGap: "20px",
    },
    textPromptAndInsertion: {
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        marginLeft: "20px",
        marginTop: "30px",
        marginBottom: "20px",
        marginRight: "20px",
    },
    inputField: {
        width: "100%",
        fontWeight: tokens.fontWeightSemibold,
    },
    listeDossiers: {
        width: "100%",
        minHeight: "300px",
        maxHeight: "300px",
        overflowY: "auto",
        marginTop: "10px",
        marginBottom: "10px",
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: "4px",
    },
    itemDossier: {
        paddingTop: "8px",
        paddingLeft: "8px",
        paddingBottom: "8px",
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground3Hover,
            cursor: "pointer",
        },
        display: "flex",
    },
    validateButton: {
        width: "100%",
        marginTop: "10px",
    },
    contentHeader: {
        marginTop: "0",
    },

    buttons: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: "10px",
    },
    example: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyItems: "center",
        minHeight: "96px",
        backgroundColor: tokens.colorNeutralBackground1,
    },

    main: {
        gap: "36px",
        display: "flex",
        flexDirection: "column",
        flexWrap: "wrap",
    },

    card: {
        width: "100%",
        height: "fit-content",
    },

    section: {
        width: "100%",
    },

    title: {margin: "0 0 12px"},

    horizontalCardImage: {
        width: "64px",
        height: "64px",
    },

    headerImage: {
        borderRadius: "4px",
        maxWidth: "44px",
        maxHeight: "44px",
    },

    caption: {
        color: tokens.colorNeutralForeground3,
    },

    text: {margin: "0"},

    selectionInfo: {
        marginBottom: "8px",
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
    },

    progressInfo: {
        marginTop: "8px",
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
    },
});

const ClassementMail: React.FC = () => {
    const styles = useStyles();
    const dossierService = useDossierService();
    const mailService = useMailService();

    const toasterId = useId("toaster");
    const {dispatchToast} = useToastController(toasterId);
    const notify = (text: string, type: "error" | "success" = "success") =>
        dispatchToast(
            <Toast appearance="inverted">
                <ToastTitle>Information</ToastTitle>
                <ToastBody>{text}</ToastBody>
            </Toast>,
            {intent: type}
        );

    const [webMail, setWebMail] = useState(new WebMails());
    const [selectedMails, setSelectedMails] = useState<SelectedMailRef[]>([]);
    const [isMultiMode, setIsMultiMode] = useState(false);
    const [checkedItems, setCheckedItems] = useState<string[]>([]);
    const [dossiersAssocie, setDossiersAssocie] = useState<number[]>([]);
    const handleCheckboxChange = (item: string) => {
        setCheckedItems((prevChecked) => {
                return prevChecked.includes(item)
                    ? prevChecked.filter((i) => i !== item)
                    : [...prevChecked, item];
            }
        );
    };

    const [isEmailAssocie, setIsEmailAssocie] = useState<boolean>(false);
    const [displaySectionPJ, setDisplaySectionPJ] = useState<boolean>(false);

    const [dossierSelectionne, setDossierSelectionne] = useState<any>(null);
    const [searchText, setSearchText] = useState<string>("");

    const [dossiers, setDossiers] = useState([]);
    const [dossiersSearch, setDossiersSearch] = useState([]);

    const [loading, setLoading] = useState(true);
    const [loadingImport, setLoadingImport] = useState(false);
    const [importProgress, setImportProgress] = useState<{current: number; total: number} | null>(null);

    const handleRechercheDossier = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);

        if (!searchText) {
            setDossiersSearch(dossiers);
        } else {
            const filteredDossiers = dossiers.filter(dossier => dossier.Nom.toLowerCase().includes(searchText.toLowerCase()));
            setDossiersSearch(filteredDossiers);
        }
    };

    const chargerDossiers = async (dossiersAssocieIds: number[]): Promise<void> => {
        setLoading(true);

        try {
            let {listeForJson} = await dossierService.donneListeDossiers();
            listeForJson = listeForJson.sort((a, b) => {
                const aPriority = dossiersAssocieIds.includes(a.IdDossier) ? 0 : 1;
                const bPriority = dossiersAssocieIds.includes(b.IdDossier) ? 0 : 1;
                return aPriority - bPriority;
            });
            setDossiers(listeForJson);
            setDossiersSearch(listeForJson);
        } catch (error) {
            notify('Erreur lors du chargement des dossiers', 'error');
        } finally {
            setLoading(false);
        }
    };

    const donneListeDossiersAssocieEmail = async (email: string) => {
        if (!email) {
            setDossiersAssocie([]);
            await chargerDossiers([]);
            return;
        }

        try {
            let result = await mailService.donneListeDossiersAssocieEmail(email);
            let dossiersIds = result.listeForJson.map((x: any) => x.idDossier);
            setDossiersAssocie(dossiersIds);

            await chargerDossiers(dossiersIds);
        } catch (error) {
            notify('Une erreur est survenue', 'error');
            await chargerDossiers([]);
        }
    };

    const preparerMailCourant = async (mailRefs: SelectedMailRef[]): Promise<void> => {
        setLoading(true);
        let refs = mailRefs;
        const multi = refs.length > 1;
        setIsMultiMode(multi);
        setSelectedMails(refs);
        setDisplaySectionPJ(false);
        setCheckedItems([]);
        setDossierSelectionne(null);
        setIsEmailAssocie(false);

        if (refs.length === 0) {
            // Toujours charger les dossiers pour ne pas bloquer l'UI
            await chargerDossiers([]);
            return;
        }

        if (multi && !isLoadItemByIdSupported()) {
            notify(
                'Le classement de plusieurs mails nécessite Outlook avec Mailbox 1.15. Un seul mail sera traité.',
                'error'
            );
            refs = [refs[0]];
            setSelectedMails(refs);
            setIsMultiMode(false);
        }

        try {
            let loaded: WebMails | null = null;

            if (refs.length === 1 && Office.context.mailbox.item) {
                try {
                    loaded = await buildWebMailFromCurrentItem();
                } catch (error) {
                    console.warn("buildWebMailFromCurrentItem failed, trying itemId:", error);
                }
            }

            if (!loaded && isLoadItemByIdSupported()) {
                loaded = await buildWebMailFromItemId(refs[0].itemId, {
                    includeAllAttachments: refs.length === 1,
                });
            }

            if (!loaded && Office.context.mailbox.item) {
                loaded = await buildWebMailFromCurrentItem();
            }

            if (!loaded) {
                notify('Impossible de charger les emails sélectionnés', 'error');
                await chargerDossiers([]);
                return;
            }

            setWebMail(loaded);
            // En multi, on évite de recharger toutes les PJ du 1er mail juste pour les dossiers
            await donneListeDossiersAssocieEmail(loaded.DeEmail || "");
        } catch (error) {
            console.error("preparerMailCourant failed:", error);
            notify('Une erreur est survenue lors du chargement de l\'email', 'error');
            await chargerDossiers([]);
        }
    };

    useEffect(() => {
        let unregister: (() => void) | undefined;
        let cancelled = false;

        Office.onReady(async () => {
            if (cancelled) {
                return;
            }

            try {
                const mailRefs = await getSelectedMailRefs();
                if (!cancelled) {
                    await preparerMailCourant(mailRefs);
                }
            } catch (error) {
                console.error(error);
                notify('Une erreur est survenue lors du chargement de l\'email', 'error');
                if (!cancelled) {
                    await chargerDossiers([]);
                }
            }

            if (cancelled || !isMultiSelectSupported()) {
                return;
            }

            unregister = registerSelectedItemsChanged(() => {
                getSelectedMailRefs()
                    .then(async (refs) => {
                        if (!cancelled) {
                            await preparerMailCourant(refs);
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        notify('Impossible de mettre à jour la sélection', 'error');
                    });
            });
        });

        return () => {
            cancelled = true;
            unregister?.();
        };
    }, []);

    if (loading) {
        return <div>Chargement ...</div>;
    }

    const selectionnerDossier = async (idDossier: number) => {
        const dossier = dossiers.find(dossier => dossier.IdDossier === idDossier);
        if (dossier) {
            webMail.IdDossier = idDossier;
            setDossierSelectionne(dossier);
            if (!isMultiMode) {
                await donneListeMailsAssociations(idDossier);
            } else {
                setIsEmailAssocie(true);
            }
        }
    };

    const enregistrerUnMail = async (mail: WebMails): Promise<boolean> => {
        const result = await mailService.enregistreWebMail(mail);
        return !!(result?.succes ?? result?.Succes);
    };

    const demarrerClassement = async () => {
        if (!dossierSelectionne) {
            return;
        }

        if (!isMultiMode && webMail.WebMailPiecesJointes.length > 0) {
            setDisplaySectionPJ(true);
            return;
        }

        await enregistrerMails();
    };

    const enregistrerMails = async () => {
        setLoadingImport(true);
        setImportProgress(null);

        const idDossier = dossierSelectionne.IdDossier;
        let successCount = 0;
        let errorCount = 0;

        try {
            if (!isMultiMode) {
                setImportProgress({current: 1, total: 1});
                const mailToSave = await buildWebMailFromCurrentItem(checkedItems);
                mailToSave.IdDossier = idDossier;

                const ok = await enregistrerUnMail(mailToSave);
                if (ok) {
                    successCount = 1;
                    await addCategoryToCurrentItem();
                    notify('Email classé avec succès !');
                } else {
                    errorCount = 1;
                    notify('Une erreur est survenue lors de l\'enregistrement de l\'email', 'error');
                }
            } else {
                const total = selectedMails.length;
                for (let i = 0; i < selectedMails.length; i++) {
                    const ref = selectedMails[i];
                    setImportProgress({current: i + 1, total});

                    try {
                        const mailToSave = await buildWebMailFromItemId(ref.itemId, {includeAllAttachments: true});
                        mailToSave.IdDossier = idDossier;
                        const ok = await enregistrerUnMail(mailToSave);
                        if (ok) {
                            successCount++;
                            await addCategoryToItemId(ref.itemId);
                        } else {
                            errorCount++;
                        }
                    } catch (error) {
                        errorCount++;
                        console.error(`Erreur classement mail ${ref.itemId}:`, error);
                    }
                }

                if (errorCount === 0) {
                    notify(`${successCount} email(s) classé(s) avec succès !`);
                } else if (successCount === 0) {
                    notify('Une erreur est survenue lors de l\'enregistrement des emails', 'error');
                } else {
                    notify(`${successCount} classé(s), ${errorCount} échec(s)`, 'error');
                }
            }

            if (successCount > 0 && errorCount === 0) {
                setTimeout(() => {
                    Office.context.ui.closeContainer();
                }, 2500);
            }
        } catch (error) {
            notify('Une erreur est survenue lors de l\'enregistrement de l\'email', 'error');
        } finally {
            setLoadingImport(false);
            setImportProgress(null);
        }
    };

    const donneListeMailsAssociations = async (idDossier: number) => {
        try {
            const result = await mailService.donneListeMailsAssociations(idDossier);
            let adressesMails = result.listeForJson.map((mail: any) => mail.email);
            setIsEmailAssocie(adressesMails.some((x: any) => x === webMail.DeEmail));
        } catch (error) {
            notify('Une erreur est survenue', 'error');
        }
    };

    const enregistreMailsAssociations = async (email: string, idDossier: number) => {
        try {
            const result = await mailService.enregistreMailsAssociations(email, idDossier);
            if (result?.succes ?? result?.Succes) {
                notify('Email associé avec succès !');
            }
        } catch (error) {
            notify('Une erreur est survenue', 'error');
        }
    };

    const labelClasser = isMultiMode
        ? `Classer ${selectedMails.length} mails dans ${dossierSelectionne?.Nom ?? 'zLawyer'}`
        : `Classer dans ${dossierSelectionne?.Nom ?? 'zLawyer'}`;

    return (
        <div>
            <Toaster toasterId={toasterId}/>

            {!displaySectionPJ && <div className={styles.textPromptAndInsertion}>
                <Text className={styles.selectionInfo}>
                    {selectedMails.length > 1
                        ? `${selectedMails.length} emails sélectionnés`
                        : selectedMails.length === 1
                            ? (selectedMails[0].subject || webMail.Sujet || '1 email sélectionné')
                            : 'Sélectionnez un ou plusieurs emails dans Outlook'}
                </Text>
                <Field className={styles.inputField} size="medium" label="Rechercher un dossier">
                    <SearchBox value={searchText} onChange={handleRechercheDossier} size={"medium"}/>
                </Field>
                <div className={styles.listeDossiers}>
                    {dossiersSearch?.map((dossier) => (
                        <div key={dossier.IdDossier}>
                            {dossiersAssocie.includes(dossier.IdDossier) ?
                                <Tooltip content="Adresse mail associée au dossier" relationship="label">
                                    <div className={styles.itemDossier}
                                         onClick={() => selectionnerDossier(dossier.IdDossier)}>
                                        <span style={{marginTop: "2px"}}>
                                            {dossier.IdDossier === dossierSelectionne?.IdDossier &&
                                                <Checkmark16Filled/>}
                                        </span>
                                        <span style={{marginTop: "2px", marginLeft: "2px", marginRight: "2px"}}>
                                            {dossiersAssocie.includes(dossier.IdDossier) && <PersonMail16Filled/>}
                                        </span>
                                        <span style={{marginLeft: "10px"}}>{dossier.Nom}</span>
                                    </div>
                                </Tooltip> :
                                <div className={styles.itemDossier}
                                     onClick={() => selectionnerDossier(dossier.IdDossier)}>
                                    <span style={{marginTop: "2px"}}>
                                        {dossier.IdDossier === dossierSelectionne?.IdDossier && <Checkmark16Filled/>}
                                    </span>
                                    <span style={{marginTop: "2px", marginLeft: "2px", marginRight: "2px"}}>
                                        {dossiersAssocie.includes(dossier.IdDossier) && <PersonMail16Filled/>}
                                    </span>
                                    <span style={{marginLeft: "10px"}}>{dossier.Nom}</span>
                                </div>}
                        </div>
                    ))}
                </div>

                {/* Multi : classement direct. Mono associé : bouton simple. Mono non associé : popover. */}
                {(isMultiMode || isEmailAssocie) && <section className={styles.section}>
                    {loadingImport && <Spinner size="small"/>}
                    {importProgress && (
                        <Text className={styles.progressInfo}>
                            Classement {importProgress.current}/{importProgress.total}...
                        </Text>
                    )}
                    <Button appearance="primary" className={styles.validateButton}
                            disabled={dossierSelectionne === null || loadingImport}
                            onClick={demarrerClassement}>
                        {labelClasser}
                    </Button>
                </section>}

                {!isMultiMode && !isEmailAssocie && <TeachingPopover>
                    <TeachingPopoverTrigger>
                        <Button appearance="primary" size="medium" className={styles.validateButton}
                                disabled={dossierSelectionne === null || loadingImport}>
                            {labelClasser}
                        </Button>
                    </TeachingPopoverTrigger>
                    <TeachingPopoverSurface>
                        <TeachingPopoverBody>
                            <TeachingPopoverTitle>Association d'une adresse email</TeachingPopoverTitle>
                            <div>
                                Souhaitez-vous mémoriser l'association entre une adresse email ci-dessous et le dossier
                                choisi afin de faciliter le traitement des mails futurs ?
                            </div>
                        </TeachingPopoverBody>
                        <TeachingPopoverFooter primary="Associer l'email"
                                               secondary="Ne pas associer"
                                               onClick={(datas) => {
                                                   if (JSON.stringify((datas.target as any).classList).includes('primary')) {
                                                       enregistreMailsAssociations(webMail.DeEmail, dossierSelectionne.IdDossier);

                                                   }

                                                   demarrerClassement();
                                               }}/>
                    </TeachingPopoverSurface>
                </TeachingPopover>}
            </div>}

            {/* Gestion des pièces jointes — mono uniquement */}
            {displaySectionPJ && !isMultiMode && <div>
                <div className={styles.root}>
                    {webMail.WebMailPiecesJointes?.length > 0 &&
                        <section className={styles.section}>
                            <Card className={styles.card}>
                                <CardHeader header={<Text weight="semibold">
                                    Séléctionnez les pièces jointes à importer
                                </Text>}/>

                                {webMail.WebMailPiecesJointes.map((attachment) => (
                                    <Checkbox key={attachment.Nom}
                                              label={attachment.Nom}
                                              checked={checkedItems.includes(attachment.Nom)}
                                              onChange={() => handleCheckboxChange(attachment.Nom)}/>
                                ))}
                            </Card>
                        </section>}
                    <section className={styles.section}>
                        {loadingImport && <Spinner size="small"/>}
                        {importProgress && (
                            <Text className={styles.progressInfo}>
                                Classement {importProgress.current}/{importProgress.total}...
                            </Text>
                        )}
                        <Button appearance="primary" className={styles.validateButton}
                                disabled={loadingImport}
                                onClick={() => enregistrerMails()}>Valider</Button>
                    </section>
                </div>
            </div>}
        </div>
    );
};

export default ClassementMail;
